/**
 * Пружина шарика нижней панели.
 *
 * Назначение файла: довести шарик до выбранного раздела так, чтобы он не приезжал
 * линейно, а осаживался. Интегратор идёт мелкими шагами, иначе жёсткая пружина
 * на длинном кадре (телефон подвис на полсекунды) разлетается в бесконечность.
 *
 * Здесь нет ни React, ни DOM: поведение проверяется тестами.
 */

export interface SpringState {
  /** Текущее положение. */
  readonly value: number;
  /** Текущая скорость, единиц в секунду. */
  readonly velocity: number;
}

export interface SpringTuning {
  /** Жёсткость: как сильно тянет к цели. */
  readonly stiffness: number;
  /** Трение: как быстро гасит колебания. */
  readonly damping: number;
}

/** Осадка после отпускания: шарик догоняет цель и слегка переваливает. */
export const SETTLE: SpringTuning = { stiffness: 180, damping: 22 };

/** Перетаскивание: шарик держится за палец, без переваливания. */
export const DRAG: SpringTuning = { stiffness: 900, damping: 60 };

/** Шаг интегратора, секунды. */
const SUBSTEP = 1 / 240;

/** Порог покоя: ближе этого к цели и медленнее этого — считаем, что приехали. */
const REST_DISTANCE = 0.05;
const REST_VELOCITY = 0.05;

/** Один шаг пружины за время dt (секунды). */
export function stepSpring(
  state: SpringState,
  target: number,
  dt: number,
  tuning: SpringTuning = SETTLE,
): SpringState {
  let { value, velocity } = state;
  let left = Math.min(dt, 0.25);

  while (left > 0) {
    const step = Math.min(SUBSTEP, left);
    const acceleration = -tuning.stiffness * (value - target) - tuning.damping * velocity;
    velocity += acceleration * step;
    value += velocity * step;
    left -= step;
  }

  return { value, velocity };
}

/** Шарик приехал: кадры можно больше не просить. */
export function isAtRest(state: SpringState, target: number): boolean {
  return (
    Math.abs(state.value - target) < REST_DISTANCE && Math.abs(state.velocity) < REST_VELOCITY
  );
}

/**
 * Скорость, приведённая к -1…1 для наклона впадины.
 * Опорная величина — ширина панели: полный размах по всей панели насыщает наклон,
 * прыжок на соседний раздел даёт примерно половину.
 */
export function normalizeVelocity(velocity: number, width: number): number {
  if (width <= 0) return 0;
  const reference = width * 1.6;
  return Math.max(-1, Math.min(1, velocity / reference));
}
